/**
 * Admin-only client-side performance monitor.
 *
 * Samples the JS heap (Chromium-only `performance.memory`) and the
 * frame loop's instantaneous FPS at a fixed cadence, logs every sample
 * to `console.debug` so devtools captures a timeline, and surfaces
 * meaningful state changes through chat callbacks (start, threshold
 * crossings, recovery). Designed to be safe to construct in every
 * client build but only `start()`-ed once the auth response confirms
 * `role === 'admin'`.
 *
 * Why a separate module:
 *   - Isolates the (small but admin-only) DOM/console plumbing from
 *     `main.ts`, which is already large.
 *   - Keeps the threshold logic, hysteresis, and chat-formatting
 *     reusable from the `/perf` chat command snapshot path.
 *
 * Why opt-in start:
 *   - Even though the data we log is harmless, free pilots have no
 *     reason to see "[perf]" lines in their devtools — those would
 *     noise up bug reports. The admin gate keeps the stream silent
 *     for everyone else.
 */

const POLL_INTERVAL_MS = 5_000;
const FPS_LOW_THRESHOLD = 30;
const FPS_LOW_RECOVER = 45;
const HEAP_HIGH_PCT = 80;
const HEAP_CRITICAL_PCT = 92;
const HEAP_RECOVER_PCT = 70;

interface MemorySample {
  /** Used JS heap in MB. */
  heapUsedMB: number;
  /** Total heap allocated by V8 in MB. */
  heapTotalMB: number;
  /** Hard heap limit configured for the renderer process in MB. */
  heapLimitMB: number;
  /** `heapUsedMB / heapLimitMB * 100`. */
  heapPct: number;
}

export interface PerfSample extends MemorySample {
  /** Instantaneous FPS over the most recent sampling window. */
  fps: number;
  /** Local timestamp the sample was taken (epoch ms). */
  ts: number;
}

type AlertLevel = 'ok' | 'warn' | 'critical';

export interface PerfMonitorOptions {
  /**
   * Called for status changes (start, threshold up/down). Wire this to
   * `chat.addActivity` so an admin reading chat sees memory regressions
   * as they happen without having to keep devtools open.
   */
  onAlert: (text: string, color?: string) => void;
  /**
   * Optional FPS provider. When unset the monitor measures FPS itself
   * via `requestAnimationFrame`; the game already drives its own loop
   * so we let the caller hand us its real frame timing when available
   * to keep the numbers consistent with the in-game debug overlay.
   */
  fpsProvider?: () => number;
}

/**
 * Read the chromium-only `performance.memory` block. Returns `null`
 * when the runtime doesn't expose it (Firefox, Safari) so callers can
 * gracefully degrade to "memory data unavailable" instead of crashing.
 */
export function readMemorySample(): MemorySample | null {
  const perf = (performance as unknown as {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  }).memory;
  if (!perf || typeof perf.usedJSHeapSize !== 'number') return null;
  const used = perf.usedJSHeapSize / 1024 / 1024;
  const total = perf.totalJSHeapSize / 1024 / 1024;
  const limit = perf.jsHeapSizeLimit / 1024 / 1024;
  const pct = limit > 0 ? (used / limit) * 100 : 0;
  return {
    heapUsedMB: +used.toFixed(1),
    heapTotalMB: +total.toFixed(1),
    heapLimitMB: +limit.toFixed(1),
    heapPct: +pct.toFixed(1),
  };
}

/**
 * Build a one-line summary of the current client perf state. Used both
 * by the periodic alert path and by the synchronous `/perf` chat
 * command (which prints the snapshot immediately, regardless of
 * thresholds).
 */
export function formatPerfLine(sample: PerfSample, prefix = 'CLIENT'): string {
  if (Number.isFinite(sample.heapLimitMB) && sample.heapLimitMB > 0) {
    return (
      `${prefix} perf · heap ${sample.heapUsedMB}/${sample.heapLimitMB}MB ` +
      `(${sample.heapPct.toFixed(0)}%) · fps ${sample.fps.toFixed(0)}`
    );
  }
  return `${prefix} perf · heap n/a (browser hides it) · fps ${sample.fps.toFixed(0)}`;
}

export class PerfMonitor {
  private readonly opts: PerfMonitorOptions;
  private timer: ReturnType<typeof setInterval> | null = null;
  private rafHandle: number | null = null;
  private frameCount = 0;
  private windowStart = 0;
  private lastFps = 60;
  private currentLevel: AlertLevel = 'ok';
  private lastFpsLowAt = 0;

  constructor(opts: PerfMonitorOptions) {
    this.opts = opts;
  }

  /**
   * Begin polling. Idempotent — calling `start` while already running
   * is a no-op so a second auth response (re-login, premium webhook
   * refresh) doesn't double up the timer.
   */
  start(): void {
    if (this.timer) return;
    this.windowStart = performance.now();
    this.frameCount = 0;
    this.tickRaf();
    const initial = this.snapshot();
    if (initial.heapLimitMB > 0) {
      this.opts.onAlert(
        `[perf] monitor on · heap limit ${initial.heapLimitMB.toFixed(0)}MB · ` +
        `every ${POLL_INTERVAL_MS / 1000}s · /perf for snapshot`,
        '#88ccff',
      );
    } else {
      this.opts.onAlert(
        `[perf] monitor on (this browser hides heap stats) · /perf for snapshot`,
        '#88ccff',
      );
    }
    console.debug('[perf] client monitor started', initial);
    this.timer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.rafHandle != null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.currentLevel = 'ok';
  }

  /** Read a fresh sample without affecting the alert state machine. */
  snapshot(): PerfSample {
    const mem = readMemorySample() ?? {
      heapUsedMB: 0, heapTotalMB: 0, heapLimitMB: 0, heapPct: 0,
    };
    const fps = this.opts.fpsProvider ? this.opts.fpsProvider() : this.lastFps;
    return { ...mem, fps, ts: Date.now() };
  }

  private tickRaf = (): void => {
    this.rafHandle = requestAnimationFrame(this.tickRaf);
    this.frameCount++;
    const now = performance.now();
    const dt = now - this.windowStart;
    if (dt >= 1_000) {
      this.lastFps = (this.frameCount * 1000) / dt;
      this.windowStart = now;
      this.frameCount = 0;
    }
  };

  private poll(): void {
    const sample = this.snapshot();
    console.debug('[perf]', sample);

    const next = this.classify(sample);
    if (next !== this.currentLevel) {
      this.emitTransition(this.currentLevel, next, sample);
      this.currentLevel = next;
    }

    if (sample.fps > 0 && sample.fps < FPS_LOW_THRESHOLD) {
      const now = sample.ts;
      // Throttle the FPS-only alert so a sustained drop fires once
      // every ~30s rather than every poll. Heap alerts use
      // hysteresis instead (see `classify`); FPS is too jittery for
      // a state machine to feel right.
      if (now - this.lastFpsLowAt > 30_000) {
        this.lastFpsLowAt = now;
        this.opts.onAlert(
          `[perf] fps low · ${sample.fps.toFixed(0)} fps (target 60)`,
          '#ffaa55',
        );
        console.warn('[perf] fps low', sample);
      }
    }
  }

  private classify(sample: PerfSample): AlertLevel {
    if (sample.heapLimitMB <= 0) return 'ok';
    if (sample.heapPct >= HEAP_CRITICAL_PCT) return 'critical';
    if (sample.heapPct >= HEAP_HIGH_PCT) return 'warn';
    if (this.currentLevel !== 'ok' && sample.heapPct <= HEAP_RECOVER_PCT) return 'ok';
    return this.currentLevel === 'ok' ? 'ok' : this.currentLevel;
  }

  private emitTransition(prev: AlertLevel, next: AlertLevel, sample: PerfSample): void {
    if (next === 'critical') {
      this.opts.onAlert(
        `[perf] heap CRITICAL · ${sample.heapUsedMB}/${sample.heapLimitMB}MB ` +
        `(${sample.heapPct.toFixed(0)}%) — consider reloading`,
        '#ff4477',
      );
      console.error('[perf] heap critical', sample);
      return;
    }
    if (next === 'warn') {
      this.opts.onAlert(
        `[perf] heap high · ${sample.heapUsedMB}/${sample.heapLimitMB}MB ` +
        `(${sample.heapPct.toFixed(0)}%)`,
        '#ffaa55',
      );
      console.warn('[perf] heap high', sample);
      return;
    }
    if (next === 'ok' && prev !== 'ok') {
      this.opts.onAlert(
        `[perf] heap recovered · ${sample.heapUsedMB}/${sample.heapLimitMB}MB ` +
        `(${sample.heapPct.toFixed(0)}%)`,
        '#55ffaa',
      );
      console.info('[perf] heap recovered', sample);
    }
  }
}
